import { useMemo
       }                                                  from "react";
import { useSelector }                                    from "react-redux";
import { useParams }                                      from "react-router-dom";
import { selectDraftRecos, 
         selectModifiedRecos, 
         selectPublishedRecos 
       }                                                  from "store/dataReducers/recommendationsSlice";
import { FormControl, 
         InputLabel, 
         MenuItem, 
         Select, 
         SelectChangeEvent,
         Box,
         Chip,
         useTheme,
         Badge,
         IconButton
       }                                                  from "@mui/material";
import NavigateNextIcon                                   from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon                                 from '@mui/icons-material/NavigateBefore';
import { BglPrimaryButton }                               from "components/button/BglButtons";
import { CreateOrEditRecoComponent }                      from "./CreateOrEditRecoComponent";
import ForumIcon                                          from '@mui/icons-material/Forum';
import { ROUTE_TREE }                                     from "routes/AppRoutes";
import { useGlobalConfig }                                from "context/GlobalContext";
import { selectRecoCommentsCount }                        from "store/dataReducers/recoCommentsSlice";

const getStatusChip = (reco : any) => {
  if (reco.editStatus === 'DRAFT') {
    return <Chip label="Draft" sx={{ width: '120px', fontSize: '1rem', height: '43px', borderColor : '#ff9800', color: '#ff9800', borderRadius: '5px' }} size="medium" variant="outlined" />
  } else if (reco.editStatus === 'MODIFIED') {
    return <Chip label="Modified" sx={{ width: '120px', fontSize: '1rem', height: '43px', borderColor : '#1976d2', color: '#1976d2', borderRadius: '5px' }} size="medium" variant="outlined" />
  } else if (reco.disabled) {
    return <Chip label="Disabled" sx={{ width: '120px', fontSize: '1rem', height: '43px', borderColor : 'grey', color: 'grey', borderRadius: '5px' }} size="medium" variant="outlined" />
  } else {
    return <Chip label="Published" sx={{ width: '120px', fontSize: '1rem', height: '43px', borderColor : '#2e7d32', color: '#2e7d32', borderRadius: '5px' }} size="medium" variant="outlined" />
  }
}

export const RecoEditPage = () => {

  const { rc } = useGlobalConfig();
  const { id } = useParams();
  const theme = useTheme();
  
  const commentCounts         = useSelector(selectRecoCommentsCount)
  const draftRecos            = useSelector(selectDraftRecos)
  const publishedRecos        = useSelector(selectPublishedRecos)
  const modifiedRecos         = useSelector(selectModifiedRecos)

  const memoizedRecos = useMemo(() => {
    const publishedRecosWithoutModifications = publishedRecos.filter(
      pubReco => modifiedRecos.find(modReco => modReco.documentId === pubReco.documentId) === undefined)
    return [...draftRecos, ...modifiedRecos, ...publishedRecosWithoutModifications]

  } ,[draftRecos, modifiedRecos, publishedRecos])
 
  const navToComments = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CMS.RECO.COMMENTS, { id });
  }

  const getRecoIdForDocId = (recoDocId: string) => {
    const reco = memoizedRecos.find(reco => reco.documentId === recoDocId)!;
    return reco.recommendationId;
  }

  const handleRecoChange = (event: SelectChangeEvent) => {
    const recoId = event.target.value;
    const recoDocId = memoizedRecos.find(reco => reco.recommendationId === recoId)!.documentId;
    rc.uiRouter.navigate(ROUTE_TREE.CMS.RECO.EDIT, { id: recoDocId, replace: true });
  };

  const handleNavigation = (direction: 'prev' | 'next') => {
    const currentIndex = memoizedRecos.findIndex(reco => reco.documentId === id);
    if (currentIndex === -1) return;

    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < memoizedRecos.length) {
      const newReco = memoizedRecos[newIndex];
      rc.uiRouter.navigate(ROUTE_TREE.CMS.RECO.EDIT, { id: newReco.documentId, replace: true });
    }
  };

  return (
    <Box sx={{ position: 'relative', textAlign: 'right', height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, ml: 2, pt: 2, width: '100%'}}>
        
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 1, mr : 4}}>
          <FormControl sx={{ width: '300px' }}>
            <InputLabel id="reco-select-label">Recommendation ID</InputLabel>
            <Select
              labelId="reco-select-label"
              id="reco-select"
              value={getRecoIdForDocId(id!)}
              label="Recommendation ID"
              size="small"
              sx={{ textAlign: 'left', padding: '4px',
                color: theme.palette.text.secondary,
                '& .MuiSelect-select': {  
                  minHeight: 'unset',
                }

              }}
              onChange={handleRecoChange}
            >
              {memoizedRecos.map((reco) => (
                <MenuItem key={reco.documentId} value={reco.recommendationId}>
                  {reco.recommendationId}
                </MenuItem>
              ))}
            </Select>
        </FormControl>
        {getStatusChip(memoizedRecos.find(reco => reco.documentId === id)!)}
        </Box>
        <BglPrimaryButton
          startIcon={<NavigateBeforeIcon />}
          onClick={() => handleNavigation('prev')}
          disabled={memoizedRecos.findIndex(reco => reco.documentId === id) === 0}
        >
          Previous
        </BglPrimaryButton>
        
        
        <BglPrimaryButton
          endIcon={<NavigateNextIcon />}
          onClick={() => handleNavigation('next')}
          disabled={memoizedRecos.findIndex(reco => reco.documentId === id) === memoizedRecos.length - 1}
        >
          Next
        </BglPrimaryButton>

        <IconButton onClick={() => navToComments()}>
          <Badge 
            badgeContent={commentCounts?.[id!] || 0} color="warning">
            <ForumIcon />
          </Badge>
        </IconButton>

      </Box>

      <CreateOrEditRecoComponent 
        editMode="update" 
        recoDocId={id!} />

    </Box>
  );
};
